import { jsPDF } from "jspdf";
import config from "../../../config";
import { getAppliedDiscount } from "../../../utils/pricingTiers";
import { resolveLogoUrl } from "../../../utils/resolveLogoUrl";

// Helper function to load image as base64
const loadImageAsBase64 = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      try {
        const dataURL = canvas.toDataURL("image/png");
        resolve(dataURL);
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = url;
  });
};

export const generateInvoicePDF = async (job, accountSettings) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  let yPos = 15;

  // Professional color palette
  const colors = {
    primary: [31, 41, 55],        // Dark slate for headers
    secondary: [107, 114, 128],   // Gray for labels
    text: [17, 24, 39],           // Near black for body text
    light: [156, 163, 175],       // Light gray for subtle text
    line: [229, 231, 235],        // Very light gray for lines
    accent: [79, 70, 229],        // Indigo accent
    success: [16, 185, 129],      // Emerald green
    error: [239, 68, 68],         // Red
    tableHeader: [249, 250, 251], // Very light gray background
  };

  // Get tax rate from account settings
  const taxRate = accountSettings?.taxRate ? parseFloat(accountSettings.taxRate) / 100 : 0.06;
  const taxPercent = (taxRate * 100).toFixed(0);

  // === HEADER SECTION ===
  let logoLoaded = false;

  // Try to load and add company logo in TOP LEFT
  if (accountSettings?.logoUrl) {
    try {
      const logoUrl = resolveLogoUrl(accountSettings.logoUrl);
      const logoBase64 = await loadImageAsBase64(logoUrl);
      // Logo in top left corner (30x30 max)
      doc.addImage(logoBase64, "PNG", margin, yPos, 25, 25);
      logoLoaded = true;
    } catch (error) {
      console.warn("Could not load logo for PDF:", error);
    }
  }

  // Company info - to the right of logo or at top left if no logo
  const companyX = logoLoaded ? margin + 30 : margin;

  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...colors.primary);
  doc.text(accountSettings?.companyName || "Company Name", companyX, yPos + 8);

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...colors.secondary);

  let infoY = yPos + 14;
  if (accountSettings?.address) {
    doc.text(accountSettings.address, companyX, infoY);
    infoY += 4;
  }
  if (accountSettings?.phone) {
    doc.text(accountSettings.phone, companyX, infoY);
    infoY += 4;
  }
  if (accountSettings?.email) {
    doc.text(accountSettings.email, companyX, infoY);
  }

  // INVOICE label on the right
  doc.setFontSize(28);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...colors.accent);
  doc.text("INVOICE", pageWidth - margin, yPos + 10, { align: "right" });

  // Invoice number below — qualified with year so invoices are distinguishable
  // across years and padded for consistent formatting.
  const invoiceYear = (job.orderDate ? new Date(job.orderDate) : new Date()).getFullYear();
  const invoiceSeq = String(job.id || 0).padStart(5, "0");
  const invoiceNumber = `INV-${invoiceYear}-${invoiceSeq}`;
  doc.setFontSize(10);
  doc.setTextColor(...colors.secondary);
  doc.text(`#${invoiceNumber}`, pageWidth - margin, yPos + 18, { align: "right" });

  yPos = Math.max(logoLoaded ? yPos + 30 : infoY, yPos + 30);
  yPos += 5;

  // Thin accent line
  doc.setDrawColor(...colors.accent);
  doc.setLineWidth(0.5);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 10;

  // === INVOICE DETAILS ROW ===
  // Left: Invoice info | Right: Bill To
  const colWidth = (pageWidth - margin * 2) / 2;

  // Invoice Details (left)
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...colors.primary);
  doc.text("INVOICE DETAILS", margin, yPos);

  // Bill To (right)
  doc.text("BILL TO", margin + colWidth + 10, yPos);
  yPos += 6;

  doc.setFont("helvetica", "normal");
  doc.setTextColor(...colors.text);
  doc.setFontSize(9);

  // Track what's missing so the PDF doesn't silently ship with blank fields.
  // Instead of empty lines, show italic "[not on file]" placeholders and collect
  // a list we can surface back to the caller.
  const c = job.customer || {};
  const missing = [];
  if (!c.name) missing.push("customer name");
  if (!c.phone) missing.push("phone");
  if (!c.addressLine1) missing.push("address");
  if (!c.city && !c.state) missing.push("city/state");

  const writeMissing = (x, y) => {
    doc.setFont("helvetica", "italic");
    doc.setTextColor(...colors.light);
    doc.text("[not on file]", x, y);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...colors.text);
  };

  // Left column details
  doc.text(`Invoice Date: ${new Date().toLocaleDateString()}`, margin, yPos);

  // Right column - customer name (bold)
  doc.setFont("helvetica", "bold");
  if (c.name) {
    doc.text(c.name, margin + colWidth + 10, yPos);
  } else {
    writeMissing(margin + colWidth + 10, yPos);
  }
  yPos += 5;

  doc.setFont("helvetica", "normal");
  doc.text(`Job #: JOB-${job.id || "000"}`, margin, yPos);

  if (c.phone) {
    doc.text(c.phone, margin + colWidth + 10, yPos);
  } else {
    writeMissing(margin + colWidth + 10, yPos);
  }
  yPos += 5;

  if (job.orderDate || job.startDate) {
    doc.text(`Invoice Date: ${new Date(job.startDate || job.orderDate).toLocaleDateString()}`, margin, yPos);
  }
  if (c.addressLine1) {
    doc.text(c.addressLine1, margin + colWidth + 10, yPos);
  } else {
    writeMissing(margin + colWidth + 10, yPos);
  }
  yPos += 5;

  if (job.fulfillmentStatus) {
    doc.text(`Status: ${job.fulfillmentStatus}`, margin, yPos);
  }
  const cityState = [c.city, c.state].filter(Boolean).join(", ");
  if (cityState) {
    doc.text(cityState, margin + colWidth + 10, yPos);
  } else {
    writeMissing(margin + colWidth + 10, yPos);
  }

  yPos += 12;

  // === PRODUCTS TABLE ===
  // Calculate product cost from job.sales
  let subtotal = 0;
  let totalQuantity = 0;
  const products = [];
  if (job.sales && Array.isArray(job.sales)) {
    job.sales.forEach((sale) => {
      if (sale.saleItems && Array.isArray(sale.saleItems)) {
        sale.saleItems.forEach((saleItem) => {
          const unitPrice = parseFloat(saleItem.unitPrice || saleItem.product?.wholesalePrice || saleItem.product?.unitPrice || 0);
          const quantity = parseInt(saleItem.quantity) || 1;
          const lineTotal = unitPrice * quantity;
          subtotal += lineTotal;
          totalQuantity += quantity;
          products.push({
            name: saleItem.product?.name || 'Unknown Product',
            quantity,
            unitPrice,
            lineTotal
          });
        });
      }
    });
  }

  // Calculate discount using the same logic as JobDetailModal and PaymentService
  // so the printed invoice matches the total the user and backend actually use.
  // Priority: job.discountType override → else no discount.
  const applied = getAppliedDiscount(job, accountSettings, totalQuantity);
  const discountPercent = applied?.discount || 0;
  const discountLabel = applied?.label ? `${applied.label} (${discountPercent}%)` : "";

  const discountAmount = subtotal * (discountPercent / 100);
  const subtotalAfterDiscount = subtotal - discountAmount;
  const taxAmount = job.includeTax ? subtotalAfterDiscount * taxRate : 0;
  const totalCost = subtotalAfterDiscount + taxAmount;

  // Table Header
  doc.setFillColor(...colors.tableHeader);
  doc.rect(margin, yPos, pageWidth - (margin * 2), 8, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...colors.primary);
  doc.text("DESCRIPTION", margin + 3, yPos + 5.5);
  doc.text("QTY", margin + 95, yPos + 5.5);
  doc.text("UNIT PRICE", margin + 115, yPos + 5.5);
  doc.text("AMOUNT", pageWidth - margin - 3, yPos + 5.5, { align: "right" });
  yPos += 10;

  // Table rows
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...colors.text);
  doc.setFontSize(9);

  // Reserve ~80mm at the bottom of each page for totals + footer.
  // If yPos would exceed pageHeight - 80, start a new page and redraw the
  // column header so long item lists don't silently truncate.
  const pageHeight = doc.internal.pageSize.getHeight();
  const itemsBottomLimit = pageHeight - 80;

  const drawTableHeader = () => {
    doc.setFillColor(...colors.tableHeader);
    doc.rect(margin, yPos, pageWidth - (margin * 2), 8, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...colors.primary);
    doc.text("DESCRIPTION", margin + 3, yPos + 5.5);
    doc.text("QTY", margin + 95, yPos + 5.5);
    doc.text("UNIT PRICE", margin + 115, yPos + 5.5);
    doc.text("AMOUNT", pageWidth - margin - 3, yPos + 5.5, { align: "right" });
    yPos += 10;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...colors.text);
    doc.setFontSize(9);
  };

  if (products.length > 0) {
    products.forEach((product, index) => {
      if (yPos + 7 > itemsBottomLimit) {
        doc.addPage();
        yPos = 15;
        drawTableHeader();
      }

      // Alternate row background
      if (index % 2 === 1) {
        doc.setFillColor(252, 252, 253);
        doc.rect(margin, yPos - 1, pageWidth - (margin * 2), 7, "F");
      }

      doc.text(product.name.substring(0, 45), margin + 3, yPos + 4);
      doc.text(String(product.quantity), margin + 95, yPos + 4);
      doc.text(`$${product.unitPrice.toFixed(2)}`, margin + 115, yPos + 4);
      doc.text(`$${product.lineTotal.toFixed(2)}`, pageWidth - margin - 3, yPos + 4, { align: "right" });
      yPos += 7;
    });
  } else {
    doc.setTextColor(...colors.light);
    doc.text("No items", margin + 3, yPos + 4);
    yPos += 7;
  }

  // If totals would get squeezed against the footer, move them to a fresh page.
  if (yPos > itemsBottomLimit) {
    doc.addPage();
    yPos = 15;
  }

  // Bottom border of table
  doc.setDrawColor(...colors.line);
  doc.setLineWidth(0.3);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 10;

  // === TOTALS SECTION ===
  const totalsX = pageWidth - 80;
  const valueX = pageWidth - margin;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...colors.text);

  // Subtotal
  doc.text("Subtotal", totalsX, yPos);
  doc.text(`$${subtotal.toFixed(2)}`, valueX, yPos, { align: "right" });
  yPos += 6;

  // Discount
  if (discountAmount > 0) {
    doc.setTextColor(...colors.success);
    doc.text(`Discount ${discountLabel}`, totalsX, yPos);
    doc.text(`-$${discountAmount.toFixed(2)}`, valueX, yPos, { align: "right" });
    doc.setTextColor(...colors.text);
    yPos += 6;
  }

  // Tax
  if (job.includeTax) {
    doc.text(`Tax (${taxPercent}%)`, totalsX, yPos);
    doc.text(`$${taxAmount.toFixed(2)}`, valueX, yPos, { align: "right" });
    yPos += 6;
  }

  // Total line separator
  yPos += 2;
  doc.setDrawColor(...colors.primary);
  doc.setLineWidth(0.5);
  doc.line(totalsX - 5, yPos, pageWidth - margin, yPos);
  yPos += 6;

  // Total
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Total", totalsX, yPos);
  doc.text(`$${totalCost.toFixed(2)}`, valueX, yPos, { align: "right" });
  yPos += 8;

  // Payments received
  let totalPaid = 0;
  if (job.payments && job.payments.length > 0) {
    totalPaid = job.payments.reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...colors.success);
    doc.text("Payments Received", totalsX, yPos);
    doc.text(`-$${totalPaid.toFixed(2)}`, valueX, yPos, { align: "right" });
    yPos += 8;
  }

  // Balance Due
  const balanceDue = totalCost - totalPaid;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);

  if (balanceDue > 0) {
    doc.setTextColor(...colors.error);
  } else {
    doc.setTextColor(...colors.success);
  }
  doc.text("Balance Due", totalsX, yPos);
  doc.text(`$${balanceDue.toFixed(2)}`, valueX, yPos, { align: "right" });
  yPos += 12;

  // === PAYMENT HISTORY (if any) ===
  if (job.payments && job.payments.length > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...colors.primary);
    doc.text("Payment History", margin, yPos);
    yPos += 5;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...colors.secondary);

    job.payments.forEach((payment) => {
      const paymentDate = payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString() : "N/A";
      const checkInfo = payment.checkNumber ? ` (Check #${payment.checkNumber})` : "";
      doc.text(
        `${paymentDate} - ${payment.paymentType || "Payment"} - $${(Number(payment.amount) || 0).toFixed(2)}${checkInfo}`,
        margin,
        yPos
      );
      yPos += 4;
    });
  }

  // === FOOTER ===
  // Position at bottom of page
  const footerY = 280;
  doc.setFontSize(9);
  doc.setTextColor(...colors.light);
  doc.text("Thank you for your business!", pageWidth / 2, footerY, { align: "center" });

  // Generate filename — use the qualified invoice number so the file on disk
  // matches what's printed on the PDF.
  const customerName = (job.customer?.name || "Customer").replace(/[^a-zA-Z0-9]/g, "_");
  const filename = `${invoiceNumber}_${customerName}.pdf`;

  // Save the PDF
  doc.save(filename);

  if (missing.length > 0) {
    console.warn(
      `Invoice ${invoiceNumber} generated with missing customer fields: ${missing.join(", ")}. ` +
      `Update the customer record for a complete invoice.`
    );
  }

  // Return a result object so callers can warn the user if customer info was
  // missing. Older callers that just expect a string still get one via toString().
  return {
    filename,
    missingFields: missing,
    toString() { return filename; },
  };
};

export default generateInvoicePDF;
